// Which desktop OS is the person on?
//
// Used only to pick which download and which install steps to show. The agent
// itself is cross-platform — it drives whichever Chrome is installed and stores
// its key in DPAPI on Windows and the Keychain on macOS — but the file to
// download differs per OS, and a screen offering a Mac download to a Windows
// user reads as "this isn't supported here".
//
// ⚠️ Presentation only. Nothing is gated on this: a wrong guess shows the wrong
// instructions, never blocks anything. That is why a plain heuristic is fine
// where fingerprinting would not be.

export const OS = {
  WINDOWS: 'windows',
  MAC: 'mac',
  LINUX: 'linux',
  UNKNOWN: 'unknown',
};

/**
 * Best-effort desktop OS detection.
 *
 * Prefers `navigator.userAgentData.platform`, which is the supported modern API
 * — `navigator.platform` is deprecated and Chrome freezes its value. Falls back
 * to the UA string, then gives up honestly rather than guessing Windows because
 * it is the most common: "unknown" shows every platform's steps, which is
 * useless-but-correct, while a confident wrong answer sends someone down the
 * wrong path.
 */
export function detectOS() {
  if (typeof navigator === 'undefined') return OS.UNKNOWN;

  const uaData = navigator.userAgentData?.platform;
  if (uaData) {
    const p = uaData.toLowerCase();
    if (p.includes('win')) return OS.WINDOWS;
    if (p.includes('mac')) return OS.MAC;
    if (p.includes('linux') || p.includes('chrome os')) return OS.LINUX;
  }

  const ua = `${navigator.userAgent || ''} ${navigator.platform || ''}`.toLowerCase();
  // Order matters: an iPad's UA can contain "mac", and Android's contains
  // "linux". Neither can run the agent, but they read as their desktop cousin
  // if the specific checks don't come first.
  if (/android|iphone|ipad|ipod/.test(ua)) return OS.UNKNOWN;
  if (/win/.test(ua)) return OS.WINDOWS;
  if (/mac/.test(ua)) return OS.MAC;
  if (/linux|x11|cros/.test(ua)) return OS.LINUX;
  return OS.UNKNOWN;
}

export const OS_LABEL = {
  [OS.WINDOWS]: 'Windows',
  [OS.MAC]: 'macOS',
  [OS.LINUX]: 'Linux',
  [OS.UNKNOWN]: 'your computer',
};

// ⚠️ AGENT_SETUP WAS REMOVED FROM HERE. It listed the per-OS terminal commands
// for building and enrolling the agent — `npm install`, `npm run deploy`,
// `npm run register-protocol`, `npm run enroll` — none of which exist any more.
// The agent now ships as one self-installing file, so the install screen has
// download steps rather than commands, and they live in AgentInstallHelp.jsx
// beside the download button they describe. Keeping a second copy here is what
// let the two drift apart in the first place.
